import { FC, Fragment, useMemo } from 'react'
import styled from 'styled-components'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import {
  IconAudioConverter,
  IconCheck,
  IconPptToVideo,
  IconTextToSpeech,
  IconVideoConverter,
  IconVideoDownloader,
} from '@/components/common/Icons'
import { poppins } from '@/components/common/fonts'
import { TOOL } from '@/constants/tools/tool'
import { useDetectDevice } from '@/hooks/useDetectDevice'

const useToolMenu = () => {
  const { t } = useTranslation('common')

  const TOOL_MENU = useMemo(
    () =>
      ({
        [TOOL.VIDEO_DOWNLOADER]: {
          tool: TOOL.VIDEO_DOWNLOADER,
          icon: <IconVideoDownloader />,
          title: t('URL Social Media Downloader'),
          description: t('Download any public video from the URL.'),
          href: '/tools/video-downloader',
        },
        [TOOL.VIDEO_CONVERTER]: {
          tool: TOOL.VIDEO_CONVERTER,
          icon: <IconVideoConverter />,
          title: t('Video Converter'),
          description: t('Convert your videos to multiple formats.'),
          href: '/tools/video-converter',
        },
        [TOOL.AUDIO_CONVERTER]: {
          tool: TOOL.AUDIO_CONVERTER,
          icon: <IconAudioConverter />,
          title: t('Audio Converter'),
          description: t('Convert your audio files to multiple formats.'),
          href: '/tools/audio-converter',
        },
        /**
         * TODO: 이 아래는 아직 안 쓰여서 다국어 처리는 나중에 함
         */
        [TOOL.PPT_TO_VIDEO]: {
          tool: TOOL.PPT_TO_VIDEO,
          icon: <IconPptToVideo />,
          title: 'PPT to Video',
          description: 'PPT to Video with automatic script generation.',
          // FIXME: URL 변경 필요
          href: 'https://naver.com',
          isExternal: true,
        },
        [TOOL.TEXT_TO_SPEECH]: {
          tool: TOOL.TEXT_TO_SPEECH,
          icon: <IconTextToSpeech />,
          title: 'Text to Speech',
          description: 'TTS to generate an AI voiceover.',
          href: '/tools/text-to-speech',
        },
        [TOOL.AI_SCRIPT_GENERATOR]: {
          tool: TOOL.AI_SCRIPT_GENERATOR,
          title: 'Text Generator',
          href: '/tools/text-generator',
        },
        [TOOL.AI_IMAGE_GENERATOR]: {
          tool: TOOL.AI_IMAGE_GENERATOR,
          title: 'AI Image Generator',
          href: '/tools/ai-image-generator',
        },
        [TOOL.AI_VIDEO_GENERATOR]: {
          tool: TOOL.AI_VIDEO_GENERATOR,
          title: 'AI Video Generator',
          href: '/tools/ai-video-generator',
        },
      } as const),
    [t],
  )

  const TOOL_MENUS = useMemo(
    () => [
      TOOL_MENU[TOOL.VIDEO_DOWNLOADER],
      TOOL_MENU[TOOL.VIDEO_CONVERTER],
      TOOL_MENU[TOOL.AUDIO_CONVERTER],
      /**
       * TODO: 추후 서비스 추가 시 주석 해제
       */
      // TOOL_MENU[TOOL.PPT_TO_VIDEO],
      // TOOL_MENU[TOOL.TEXT_TO_SPEECH],
      // TOOL_MENU[TOOL.AI_SCRIPT_GENERATOR],
      // TOOL_MENU[TOOL.AI_IMAGE_GENERATOR],
      // TOOL_MENU[TOOL.AI_VIDEO_GENERATOR],
    ],
    [TOOL_MENU],
  )

  return {
    TOOL_MENU,
    TOOL_MENUS,
  }
}

type MenuProps = {
  className?: string
}

export const Menu: FC<MenuProps> = ({ className }) => {
  const { t } = useTranslation('common')
  const router = useRouter()

  const { TOOL_MENUS } = useToolMenu()
  const { isMobile } = useDetectDevice()
  const currentHref = router.pathname
  const advancedEditorHref = isMobile ? '/dashboard' : '/tools/advanced-editor'

  return (
    <MenuContainer className={className}>
      {TOOL_MENUS.map((toolMenu) => {
        const icon = 'icon' in toolMenu && toolMenu.icon
        const isCurrentMenu = toolMenu.href === currentHref
        const description =
          'description' in toolMenu ? toolMenu.description : '-'

        return (
          <Fragment key={toolMenu.tool}>
            <MenuLink href={toolMenu.href}>
              <MenuLinkTitleContainer>
                {!!icon && <MenuLinkIcon>{icon}</MenuLinkIcon>}

                <MenuLinkTitle>{toolMenu.title}</MenuLinkTitle>

                {isCurrentMenu && (
                  <MenuLinkCheck>
                    <IconCheck />
                  </MenuLinkCheck>
                )}
              </MenuLinkTitleContainer>

              <MenuLinkDescription>{description}</MenuLinkDescription>
            </MenuLink>

            <HorizontalBar />
          </Fragment>
        )
      })}

      <AdvancedEditorArea>
        <AdvancedEditorDescription>
          {t('Need more AI tools?')}
        </AdvancedEditorDescription>
        <AdvancedEditorButton href={advancedEditorHref}>
          {t('Advanced Editor')}
        </AdvancedEditorButton>
      </AdvancedEditorArea>
    </MenuContainer>
  )
}

const MenuContainer = styled.div`
  display: flex;
  flex-flow: column;
`

const MenuLink = styled(Link)`
  align-self: stretch;
  display: flex;
  flex-flow: column;
  gap: 6px;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  &:hover {
    background-color: #f0f2f5;
    color: #000000;
  }
  transition: background-color 0.2s;
  color: #000000;
`

const MenuLinkTitleContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: space-between;
`

const MenuLinkIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 20px;
  height: 20px;
`

const MenuLinkTitle = styled.div`
  flex-grow: 1;

  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
`

const MenuLinkCheck = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const MenuLinkDescription = styled.div`
  font-size: 11px;
  line-height: 16.5px;
`

const HorizontalBar = styled.div`
  height: 1px;
  margin: 0 16px;
  background: #f0f2f5;
`

const AdvancedEditorArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  padding: 16px;
`

const AdvancedEditorDescription = styled.div`
  font-family: ${poppins.string};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
`

const AdvancedEditorButton = styled(Link)`
  padding: 10px;
  height: auto;

  background-color: #4276ee;
  box-shadow: 0px 3px 6px 0px #007c8933;
  border-radius: 10px;

  font-family: ${poppins.string};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  color: #ffffff;

  transition: opacity 0.2s;

  &:hover {
    color: #ffffff;

    opacity: 0.8;
  }
`
